import React, { useState, useMemo } from 'react';
import { Dataset, DataRelationship } from '../types';

// Simple relationship graph with draggable dataset nodes
const RelationshipGraph: React.FC<{ datasets: Dataset[], relationships: DataRelationship[] }> = ({ datasets, relationships }) => {
    const width = 800;
    const height = 360;
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(width, height) / 2 - 60;

    const [positions, setPositions] = useState<Map<string, { x: number; y: number }>>(() => new Map());
    const [drag, setDrag] = useState<{ id: string; offX: number; offY: number } | null>(null);
    const [panning, setPanning] = useState<{ x: number; y: number } | null>(null);
    const [scale, setScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);

    const defaults = useMemo(() => {
        const m = new Map<string, { x: number; y: number }>();
        datasets.forEach((d, i) => {
            const angle = (2 * Math.PI * i) / (datasets.length || 1);
            m.set(d.id, { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
        });
        return m;
    }, [datasets]);

    const nodes = datasets.map(d => ({ id: d.id, name: d.name, x: positions.get(d.id)?.x ?? defaults.get(d.id)!.x, y: positions.get(d.id)?.y ?? defaults.get(d.id)!.y }));
    const byId = new Map(nodes.map(n => [n.id, n] as const));

    // Build strict inferred edges (exact same-name key)
    const inferredPairs = new Map<string, string>();
    for (let i = 0; i < datasets.length; i++) {
        for (let j = i + 1; j < datasets.length; j++) {
            const A = datasets[i], B = datasets[j];
            let label = '';
            outer: for (const ac of A.schema) {
                for (const bc of B.schema) {
                    const sameName = ac.name.toLowerCase() === bc.name.toLowerCase();
                    const bothNumeric = ac.type === 'number' && bc.type === 'number';
                    const bothStringLike = (ac.type !== 'number') && (bc.type !== 'number');
                    if (sameName && (bothNumeric || bothStringLike)) { label = `${A.name}.${ac.name} = ${B.name}.${bc.name}`; break outer; }
                }
            }
            if (label) inferredPairs.set([A.id, B.id].sort().join('|'), label);
        }
    }

    // Explicit edges (may be multiple per pair)
    const explicitPairs = new Map<string, string>();
    relationships.forEach(r => {
        const key = [r.datasetAId, r.datasetBId].sort().join('|');
        const A = datasets.find(d => d.id === r.datasetAId);
        const B = datasets.find(d => d.id === r.datasetBId);
        if (!A || !B) return;
        const lbl = `${A.name}.${r.fieldA} = ${B.name}.${r.fieldB}`;
        explicitPairs.set(key, explicitPairs.has(key) ? `${explicitPairs.get(key)} • ${lbl}` : lbl);
    });

    // Merge edges, prefer explicit label if present
    const edges: { a: string; b: string; label: string }[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < datasets.length; i++) {
        for (let j = i + 1; j < datasets.length; j++) {
            const key = [datasets[i].id, datasets[j].id].sort().join('|');
            const label = explicitPairs.get(key) ?? inferredPairs.get(key);
            if (label && !seen.has(key)) {
                seen.add(key);
                edges.push({ a: key.split('|')[0], b: key.split('|')[1], label });
            }
        }
    }

    const toWorld = (px: number, py: number) => ({ x: (px - tx) / scale, y: (py - ty) / scale });
    const onNodeDown = (id: string, e: React.MouseEvent<SVGCircleElement>) => {
        const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const world = toWorld(px, py);
        const p = byId.get(id);
        if (!p) return;
        setDrag({ id, offX: world.x - p.x, offY: world.y - p.y });
    };
    const onMouseMove: React.MouseEventHandler<SVGSVGElement> = (e) => {
        const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        if (drag) {
            const world = toWorld(px, py);
            const nx = world.x - drag.offX;
            const ny = world.y - drag.offY;
            setPositions(prev => { const m = new Map(prev); m.set(drag.id, { x: nx, y: ny }); return m; });
        } else if (panning) {
            setTx(t => t + e.movementX);
            setTy(t => t + e.movementY);
        }
    };
    const onMouseUp: React.MouseEventHandler<SVGSVGElement> = () => { setDrag(null); setPanning(null); };
    const onBgDown = () => setPanning({ x: 0, y: 0 });
    const onWheel: React.WheelEventHandler<SVGSVGElement> = (e) => {
        e.preventDefault();
        const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const prev = scale;
        const factor = Math.exp((-e.deltaY) * 0.001);
        const next = Math.min(3, Math.max(0.5, prev * factor));
        if (next === prev) return;
        const wx = (px - tx) / prev;
        const wy = (py - ty) / prev;
        setScale(next);
        setTx(px - wx * next);
        setTy(py - wy * next);
    };

    // Helper to render a wrapped label box near edge midpoint
    const renderEdgeLabel = (ax: number, ay: number, bx: number, by: number, text: string) => {
        const mx = (ax + bx) / 2, my = (ay + by) / 2;
        const dx = bx - ax, dy = by - ay; const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = -dy / len, ny = dx / len; const offset = 18;
        const cxLabel = mx + nx * offset, cyLabel = my + ny * offset;
        const wrap = (s: string, max = 40): string[] => {
            if (s.length <= max) return [s];
            const mid = Math.floor(s.length / 2);
            const breakChars = [' • ', ' = ', ' '];
            for (const bc of breakChars) { const b = s.lastIndexOf(bc, mid); if (b > 0) return [s.slice(0, b).trim(), s.slice(b).trim()]; const a = s.indexOf(bc, mid); if (a > 0) return [s.slice(0, a).trim(), s.slice(a).trim()]; }
            return [s.slice(0, max).trim(), s.slice(max).trim()];
        };
        const lines = wrap(text);
        const longest = lines.reduce((m, l) => Math.max(m, l.length), 0);
        const charW = 7, padX = 12, padY = 8, lineH = 14;
        const rectW = Math.max(60, longest * charW + padX * 2);
        const rectH = lines.length * lineH + padY * 2;
        const rectX = cxLabel - rectW / 2, rectY = cyLabel - rectH / 2;
        return (
            <g>
                <rect x={rectX} y={rectY} width={rectW} height={rectH} rx={6} fill="rgba(2, 4, 8, 0.9)" opacity={0.95} stroke="#f59e0b" strokeWidth={1} />
                <text x={cxLabel} y={cyLabel - (lines.length - 1) * (lineH / 2)} textAnchor="middle" fontSize={11} fill="#f59e0b" fontWeight="bold">
                    {lines.map((ln, i) => (<tspan key={i} x={cxLabel} dy={i === 0 ? 0 : lineH}>{ln}</tspan>))}
                </text>
            </g>
        );
    };

    return (
        <svg width={width} height={height} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onWheel={onWheel} className="block mx-auto">
            <g transform={`translate(${tx},${ty}) scale(${scale})`}>
                <rect x={-2000} y={-2000} width={4000} height={4000} fill="transparent" onMouseDown={onBgDown} />
                {edges.map((e, idx) => {
                    const a = byId.get(e.a), b = byId.get(e.b); if (!a || !b) return null;
                    return (
                        <g key={idx}>
                            <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#f59e0b" strokeWidth={2} strokeOpacity={0.6} />
                            {renderEdgeLabel(a.x, a.y, b.x, b.y, e.label)}
                        </g>
                    );
                })}
                {nodes.map(n => (
                    <g key={n.id}>
                        <circle cx={n.x} cy={n.y} r={24} fill="#f59e0b" opacity={0.15} />
                        <circle cx={n.x} cy={n.y} r={22} fill="#020408" stroke="#f59e0b" strokeWidth={2} onMouseDown={(e) => onNodeDown(n.id, e)} style={{ cursor: 'grab' }} />
                        <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize={12} fill="#e2e8f0" fontWeight="500">{n.name}</text>
                    </g>
                ))}
            </g>
        </svg>
    );
};

export default RelationshipGraph;
